import type { RichTextSegment } from "#lib/web-ui/blocks/glossary/index.js";
import { associateAnswers } from "@fergmar/contracts/public/associate-leads";

export const ASSOCIATE_FORM_ANCHOR = "where-you-stand";

export const ASSOCIATE_OPTIONS_ANCHOR = "options";

export const ASSOCIATE_PROGRAM_ANCHOR = "program";

export const associateHeader = {
	formButtonLabel: "See where I stand"
};

export const associateContactCard = {
	quote: "I'll give you a straight read on your options, including telling you when selling to us isn't the right move.",
	directLabel: "Direct",
	emailLabel: "Email",
	webLabel: "Web",
	callOrTextLabel: "Call or text",
	callLabel: "Call",
	emailButtonLabel: "Email me"
};

export function associateMeta(input: {
	displayName: string;
	title: string;
	siteName: string;
	city: string;
}) {
	return {
		title: `${input.displayName} | Selling your house in ${input.city} | ${input.siteName}`,
		description: `${input.displayName}, ${input.title} at ${input.siteName}. Compare a cash offer, the Equity Protection Program, and the open market, and see where you stand.`
	};
}

export const associateHero = {
	headline: "Thinking about selling? There's more than one way to do it.",
	fineText: "Free, no obligation, nothing to arrange.",
	formLinkLabel: "Or answer five questions and we'll tell you where you stand"
};

export function associateHeroLocation(input: { city: string; state: string }) {
	return `${input.city}, ${input.state}`;
}

export function associateHeroParagraph(siteName: string) {
	return `${siteName} is a local company. Most of what we do is buy houses for cash, as-is, fast. For sellers who have time and want to chase a stronger number, we also run the Equity Protection Program. And sometimes the open market is the right answer.`;
}

export function associateHeroCards(firstName: string) {
	return [
		{
			eyebrow: "Start here",
			title: "I'm new to this",
			description: "What Fergmar does and which option fits.",
			href: `#${ASSOCIATE_OPTIONS_ANCHOR}`
		},
		{
			eyebrow: "Skip ahead",
			title: `${firstName} sent me this`,
			description: "Straight to how the Equity Protection Program works.",
			href: `#${ASSOCIATE_PROGRAM_ANCHOR}`
		}
	];
}

export function associateMetrics(input: { years: string; assets: string; serviceArea: string }) {
	return {
		stats: [
			{ value: input.years, label: `Years in ${input.serviceArea}` },
			{ value: "1,000s", label: "Sellers helped" },
			{ value: input.assets, label: "Assets under management" },
			{ value: "A+", label: "BBB rating" }
		]
	};
}

export const associateOptions = {
	title: "Everything we can do for you.",
	subtitle:
		"Most people find us looking for a cash offer, and most of the time that is exactly the right call. But it isn’t the only thing we do, so it is worth knowing what else is on the table.",
	cards: [
		{
			title: "We buy it for cash",
			icon: "building",
			description:
				"This is what we do most. A firm cash offer on your house as-is, usually within 24 hours, no repairs, no access to arrange, no fees, no financing that can fall through. Close in as little as a week, or on whatever schedule suits you. The right answer for most people who call us."
		},
		{
			title: "The Equity Protection Program",
			icon: "shield",
			description:
				"For sellers who can trade some time for a stronger number. We agree your price up front and protect it in writing, then market the house and find the buyer. Nothing out of your pocket. Worth a look if you're not in a hurry."
		},
		{
			title: "Take it to the open market",
			icon: "line-chart",
			description:
				"The traditional route, and often the right one. If you already have an agent, keep them. We work with agents across the Crossroads all the time. If you don't, we can help you get there or point you to someone good. Best when the house shows well and you have time."
		}
	],
	note: "We buy from agents, sell to agents, and work alongside them every week. If the open market is the right answer for you, we’ll say so plainly. That is not a loss for us."
};

export const associateComparison = {
	title: "Three situations, three different solutions.",
	subtitle:
		"Which one is right has nothing to do with which number sounds biggest. No asterisks. Where a path costs you something, it says so.",
	rowHeaderLabel: "What matters",
	columns: ["We buy it", "Program", "Open market"],
	rows: [
		{
			label: "What you're paid",
			cells: [
				[{ text: "A firm price, agreed today" }],
				[{ text: "Agreed & protected" }],
				[{ term: "gross", text: "Often the highest" }]
			]
		},
		{
			label: "How long",
			cells: [
				[{ text: "About a week" }],
				[{ text: "90 to 100 days" }],
				[{ text: "3 to 6 months" }]
			]
		},
		{
			label: "Repairs",
			cells: [[{ text: "None" }], [{ text: "None" }], [{ text: "Usually the seller’s" }]]
		},
		{
			label: "Access",
			cells: [
				[{ text: "None" }],
				[{ text: "We arrange it" }],
				[{ text: "Part of the process" }]
			]
		},
		{
			label: "Fees",
			cells: [
				[{ text: "None" }],
				[{ text: "At closing" }],
				[{ term: "commission", text: "Up to 6%" }]
			]
		},
		{
			label: "Finds the buyer",
			cells: [
				[{ text: "We are the buyer" }],
				[{ text: "We do" }],
				[{ text: "Your listing agent" }]
			]
		}
	],
	note: "None of these columns is the good one. Which is right depends entirely on what you need, and that is a conversation, not a chart."
} satisfies {
	title: string;
	subtitle: string;
	rowHeaderLabel: string;
	columns: string[];
	rows: { label: string; cells: RichTextSegment[][] }[];
	note: string;
};

export const associateDeductions = {
	title: "The price is not what you keep.",
	lede: [
		{
			text: "Every path has things that come off the top before the money reaches you. The headline number and the "
		},
		{ term: "netproceeds", text: "number you walk away with" },
		{
			text: " are rarely the same one, and every one of those deductions comes out of your "
		},
		{ term: "equity", text: "equity" },
		{ text: "." }
	],
	hint: "These are the words that cost people money because nobody explained them.",
	costLabel: "Cost",
	noCostLabel: "No cost",
	columns: [
		{
			title: "Taking it to the open market",
			items: [
				{ cost: true, segments: [{ term: "commission", text: "Agent commissions" }] },
				{ cost: true, segments: [{ term: "concessions", text: "Buyer concessions" }] },
				{ cost: true, segments: [{ text: "Repairs before it goes up" }] },
				{
					cost: true,
					segments: [
						{ text: "Cleaning, " },
						{ term: "staging", text: "staging" },
						{ text: ", photos" }
					]
				},
				{ cost: true, segments: [{ term: "credits", text: "Inspection repair credits" }] },
				{
					cost: true,
					segments: [{ term: "holding", text: "Months of taxes, insurance, utilities" }]
				},
				{
					cost: true,
					segments: [{ term: "appraisal", text: "Appraisal and financing fallout risk" }]
				}
			]
		},
		{
			title: "Equity Protection Program",
			items: [
				{ cost: false, segments: [{ text: "No agent commission charged to you" }] },
				{ cost: false, segments: [{ text: "No repairs required" }] },
				{ cost: false, segments: [{ text: "No cleaning, staging, or photo cost" }] },
				{ cost: false, segments: [{ text: "No marketing cost" }] },
				{ cost: false, segments: [{ text: "No up-front money of any kind" }] },
				{
					cost: true,
					segments: [{ text: "Our fee, disclosed on the settlement statement" }]
				}
			]
		},
		{
			title: "Selling to us for cash",
			items: [
				{ cost: false, segments: [{ text: "No commissions or fees" }] },
				{ cost: false, segments: [{ text: "No repairs" }] },
				{ cost: false, segments: [{ text: "Almost no holding time" }] },
				{
					cost: true,
					segments: [
						{ text: "Priced for speed and certainty rather than the open market" }
					]
				}
			]
		}
	],
	note: "Your amount under the program is set before we market anything. It does not change based on what your buyer eventually pays."
} satisfies {
	title: string;
	lede: RichTextSegment[];
	hint: string;
	costLabel: string;
	noCostLabel: string;
	columns: { title: string; items: { cost: boolean; segments: RichTextSegment[] }[] }[];
	note: string;
};

export const associateProgram = {
	title: "How the program runs.",
	subtitle:
		"The next few sections go deep on the Equity Protection Program, because it is the one most people have never heard of. A cash offer and the open market explain themselves. This one needs a walkthrough.",
	timelineLead: "Roughly 100 days from the day we start to the day you sign at closing.",
	timelineAlt:
		"About 55 percent of the program is marketing and finding your buyer. The remaining 45 percent is under contract through closing.",
	timelineSegments: [
		{ label: "Marketing and finding your buyer", percent: 55 },
		{ label: "Under contract through closing", percent: 45 }
	],
	timelineNote: [
		{ text: "Most buyers close within 30 to 45 days once they're " },
		{ term: "undercontract", text: "under contract" },
		{ text: "." }
	],
	backLabel: "Back",
	nextLabel: "Next step",
	steps: [
		{
			label: "Walk it",
			title: "We look at the property and the numbers",
			body: [
				{
					text: "We walk it, assess what actually needs attention rather than assuming the worst, and pull recent "
				},
				{ term: "comps", text: "closed sales" },
				{
					text: ", not asking prices, not an online estimate, and not what it would be worth "
				},
				{ term: "arv", text: "fully renovated" },
				{ text: "." }
			]
		},
		{
			label: "Agree your number",
			title: "We agree on your number, in writing",
			body: [
				{
					text: "Before anything is marketed, we agree on the amount you receive. That number is fixed and protected by the agreement."
				}
			]
		},
		{
			label: "Market it",
			title: "We market the property",
			body: [
				{
					text: "Professional photos, advertising, social channels, and direct outreach to our investor and buyer network. You give us reasonable access, usually a "
				},
				{ term: "lockbox", text: "lockbox" },
				{ text: ", and keep utilities on." }
			]
		},
		{
			label: "Handle buyers",
			title: "We handle the buyers",
			body: [
				{
					text: "We field offers, sort the credible from the not, negotiate, and coordinate inspections. You don't screen anyone or take those calls."
				}
			]
		},
		{
			label: "Close",
			title: "We coordinate to closing",
			body: [
				{ text: "Contract, " },
				{ term: "title", text: "title" },
				{
					text: ", documentation, and communication all the way through. A transaction coordinator keeps you updated at each stage. You show up and sign."
				}
			]
		}
	]
} satisfies {
	title: string;
	subtitle: string;
	timelineLead: string;
	timelineAlt: string;
	timelineSegments: { label: string; percent: number }[];
	timelineNote: RichTextSegment[];
	backLabel: string;
	nextLabel: string;
	steps: { label: string; title: string; body: RichTextSegment[] }[];
};

export function associateStepCountLabel(current: number, total: number) {
	return `Step ${current} of ${total}`;
}

export const associateHandles = {
	title: "What we take on. What's left for you.",
	columns: [
		{
			title: "Fergmar handles",
			items: [
				"Property assessment and pricing",
				"Professional marketing photos",
				"Advertising and social media exposure",
				"Outreach to our buyer and investor network",
				"Arranging access and answering buyer questions",
				"Screening and negotiating offers",
				"Coordinating inspections",
				"Contract, title, and closing coordination",
				"Cleanout assistance where applicable",
				"Every dollar of marketing and program cost"
			]
		},
		{
			title: "You handle",
			items: [
				"Leaving utilities on if they’re already active",
				"Giving reasonable access to your home",
				"Signing the program agreement",
				"Signing at closing"
			]
		}
	],
	note: "That's the trade. You give us time and access. We do the work and carry the cost."
};

export const associateCapabilities = {
	title: "What we bring to the program.",
	cards: [
		{
			title: "A real buyer network",
			icon: "users",
			description:
				"Investors with different strategies, rentals, renovations, land, specific neighborhoods. A house that doesn't fit one buyer's plan fits another's perfectly."
		},
		{
			title: "Real construction experience",
			icon: "building",
			description:
				"We price repairs off what work actually costs, not a guess from the driveway. That cuts both ways, sometimes a problem is smaller than you were told, sometimes it is genuinely bigger. Either way the number you get from us is grounded in real work."
		},
		{
			title: "In-house marketing",
			icon: "bar-chart-3",
			description:
				"Photography, advertising, and market exposure handled by our own team, at our cost, not yours."
		},
		{
			title: "Transaction coordination",
			icon: "arrow-up-right",
			description:
				"A dedicated coordinator who keeps you updated from contract through closing, so you're never guessing where things stand."
		}
	]
};

export function associateCapabilitiesSubtitle(input: { siteName: string; city: string }) {
	return `${input.siteName} is part of Fergmar Enterprises, a ${input.city} company that buys, renovates, manages and sells real estate here. Not a call center in another state. When we take on a property under the program, this is what goes to work on it.`;
}

export function associateCitiesLead(serviceArea: string) {
	return `We buy and sell across ${serviceArea}:`;
}

export const associateFaq = {
	title: "Straight answers.",
	items: [
		{
			question: "What does it cost me?",
			answer: "Nothing up front. We pay for the marketing, the photos, the manpower, and the coordination. Our compensation comes out of the transaction itself and is disclosed on the settlement statement at closing. If we don't produce a sale, we don't get paid."
		},
		{
			question: "What if you can't find a buyer?",
			answer: "Then at the end of the program term you can walk away or choose to extend. This isn't a six-month or twelve-month commitment. If we can't perform inside the window we agreed to, we part ways with no obligation and no hard feelings."
		},
		{
			question: "Why does this take 90 to 100 days?",
			answer: "Because we're asking the market to pay more than a quick-close buyer would, and more price takes more exposure. That window covers marketing, buyer access, inspections, negotiation, title work, and closing. Many properties move faster, but we won't promise a date we can't control."
		},
		{
			question: "Does my number change if you sell it for more?",
			answer: "No. The amount you receive is agreed before we market the property and is protected by the written agreement. That's the point of the program. You get certainty on your side while we take on the execution risk."
		},
		{
			question: "Is Fergmar buying my house?",
			answer: "Not necessarily, and we'll tell you plainly which it is. Under this program our job is to find the right end buyer. Sometimes that's us. Often it's someone in our network whose plans for the property support a better number than our own buying criteria would."
		},
		{
			question: "How do I know which program is right for me?",
			answer: "That's what the three questions at the top are for, and it's the first thing we'll talk through on a call. Cash solves for speed and certainty. The program solves for price when you don't want the work. If neither fits, we'll say so."
		},
		{
			question: "I already have an agent. Does that rule this out?",
			answer: "Not at all. We work with agents across the Crossroads constantly. We buy from them, we sell to them, and we cooperate on deals every week. If you're under a listing agreement, tell us and we'll work with your agent rather than around them. If you're an agent with a seller who needs something other than the open market, call us directly."
		},
		{
			question: "Who is this not right for?",
			answer: "If you need to be closed and funded in the next week or two, this isn't the fit. Ask about a cash offer instead. And if the amount you need already sits at the very top of what the market will realistically pay for the property as it stands today, there isn't enough room for the program to work. We'd rather tell you that than sell you a number we can't deliver."
		}
	]
};

export const associateFooterDisclaimer = {
	glossaryLabel: "Glossary of terms"
};

export function associateFooterDisclaimerText(input: { siteName: string; year: number }) {
	return `This page is general information about the Equity Protection Program and is not an offer, a listing agreement, or a commitment to purchase. Program terms, the agreed seller proceeds, the program period, and all rights and obligations of each party are governed solely by the written agreement signed by the parties. Dollar figures used in the glossary are illustrative examples only, not quotes, estimates or appraisals of any specific property. Company figures are as published by Fergmar Enterprises, LLC. © ${input.year} ${input.siteName}, a Fergmar Enterprises, LLC company.`;
}

export const associateForm = {
	title: "Where do you actually stand?",
	subtitle:
		"Answer honestly. If we aren't the right move for you, this will say so, and then tell you what is.",
	glossaryHintBefore: "Tap any underlined word for a plain-English explanation, or ",
	glossaryHintLink: "open the glossary",
	glossaryHintAfter: ".",
	stepLabel: (step: number, total: number) => `Question ${step} of ${total}`,
	backLabel: "Back",
	continueLabel: "Continue",
	submitLabel: "See where I stand",
	submittingLabel: "Sending...",
	errorToast: "We could not send your answers. Please try again.",
	privacyNote: (firstName: string, siteName: string) =>
		`What you write here goes only to ${firstName} at ${siteName}. We don't sell or share it, and writing to us doesn't commit you to anything.`,
	situation: {
		question: "What's the situation with the property?",
		otherPrompt:
			"Tell us about the property and your situation: inherited, rental, vacant, anything.",
		answers: associateAnswers.situation
	},
	timeline: {
		question: "How soon would you want to be done?",
		otherPrompt: "What's driving your timeline?",
		answers: associateAnswers.timeline
	},
	workLevel: {
		question: "How much work would you put into it?",
		otherPrompt: "What would you be willing to do, or what's stopping you?",
		answers: associateAnswers.workLevel
	},
	address: {
		question: "Which property?",
		help: "We use the address to pull recent closed sales nearby. Nothing happens to the property and nobody shows up at the door.",
		label: "Property address",
		placeholder: "Start typing the address"
	},
	contact: {
		question: "Last thing. Where do we send your result?",
		nameLabel: "Your name",
		phoneLabel: "Phone",
		emailLabel: "Email"
	},
	result: {
		eyebrow: "Your result",
		hasYourAnswers: (firstName: string) =>
			`${firstName} has your answers and will contact you.`,
		callLabel: (firstName: string) => `Call ${firstName}`,
		textLabel: (firstName: string) => `Text ${firstName}`,
		smsBody: (firstName: string, address: string) =>
			`Hi ${firstName}, I just answered the questions on your page. Property: ${address}.`
	}
};
